import {
  PALETTE_IDS,
  PALETTE_LABELS,
  SEATED_HEIGHT_RANGE,
  SHADOW_LABELS,
  SHADOW_QUALITIES,
  SNAP_TURN_CHOICES,
  TABLET_TEXT_SIZES,
  TEXT_LABELS,
  type Settings,
  type SnapTurnDegrees,
} from '../settings';

interface Props {
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
}

/** The right stick at the table in VR: a step of so many degrees, or free turning. */
type Turning = SnapTurnDegrees | 'free';
const TURNINGS: readonly Turning[] = [...SNAP_TURN_CHOICES, 'free'];

/** A row of radio buttons in the menu's `.choice` style. */
function Choice<T extends string | number | boolean>({
  label,
  name,
  choices,
  value,
  render,
  onPick,
}: {
  label: string;
  /** For test ids: `setting-<name>-<choice>`. */
  name: string;
  choices: readonly T[];
  value: T;
  render: (choice: T) => string;
  onPick: (choice: T) => void;
}) {
  const key = (choice: T) =>
    typeof choice === 'boolean' ? (choice ? 'on' : 'off') : String(choice);
  return (
    <div className="choice" role="radiogroup" aria-label={label}>
      {choices.map((choice) => (
        <button
          key={key(choice)}
          role="radio"
          aria-checked={value === choice}
          className={value === choice ? 'active' : ''}
          data-testid={`setting-${name}-${key(choice)}`}
          onClick={() => onPick(choice)}
        >
          {render(choice)}
        </button>
      ))}
    </div>
  );
}

/** How the table looks and how VR feels: graphics, comfort, colours and lettering. */
export function LookSettings({ settings, onChange }: Props) {
  const { graphics, comfort } = settings;
  const seated = `${comfort.seatedHeight > 0 ? '+' : ''}${Math.round(comfort.seatedHeight * 100)} cm`;
  return (
    <>
      <div className="menu-row">
        <span className="menu-label">Shadows</span>
        <Choice
          label="Shadow quality"
          name="shadows"
          choices={SHADOW_QUALITIES}
          value={graphics.shadows}
          render={(q) => SHADOW_LABELS[q]}
          onPick={(shadows) => onChange({ graphics: { ...graphics, shadows } })}
        />
      </div>
      <p className="hint">
        The pieces' shadows on the board and the fighters' in the arena. The headset never goes
        above Low, and Off skips the shadow pass altogether.
      </p>

      <div className="menu-row">
        <span className="menu-label">Reflections</span>
        <Choice
          label="Board reflections"
          name="reflections"
          choices={[true, false]}
          value={graphics.reflections}
          render={(on) => (on ? 'On' : 'Off')}
          onPick={(reflections) => onChange({ graphics: { ...graphics, reflections } })}
        />
      </div>
      <p className="hint">
        The pieces mirrored in the polished board, on a desktop only: the headset keeps a still
        reflection of the sky.
      </p>

      <p className="hint">
        Quest tabletop: point and pull either trigger to select. Left stick moves forward/back along
        your gaze (including up/down) or sideways relative to your view. Flick the right stick left
        or right to turn by a step. Press A/X, click either thumbstick, or select Reset View in the
        immersive Menu to return upright to your original seat. Move gently if you are sensitive to
        motion. During a duel, either stick moves your fighter, and with both pushed the right one
        aims; hold fire to aim/bend a Wizard or Sorceress shot with the right stick. Triggers attack
        and A/X dashes; hold B or Y to give the duel up. Your viewpoint stays still. Hold a grip to
        point at the radio and VR controls during a duel.
      </p>

      <div className="menu-row">
        <span className="menu-label">Turning</span>
        <Choice
          label="Right stick turning in VR"
          name="turning"
          choices={TURNINGS}
          value={comfort.freeTurning ? 'free' : comfort.snapTurnDegrees}
          render={(turn) => (turn === 'free' ? 'Free' : `${turn}°`)}
          onPick={(turn) =>
            onChange({
              comfort:
                turn === 'free'
                  ? { ...comfort, freeTurning: true }
                  : { ...comfort, freeTurning: false, snapTurnDegrees: turn },
            })
          }
        />
      </div>
      <p className="hint">
        A flick of the right stick turns you a step, level with the floor: the gentlest on the
        stomach. Free turns smoothly toward any side of your view, up and down too, through full
        loops; you can end up upside down.
      </p>

      <div className="menu-row">
        <span className="menu-label">Seated play</span>
        <label className="slider">
          Height
          <input
            type="range"
            min={-SEATED_HEIGHT_RANGE}
            max={SEATED_HEIGHT_RANGE}
            step={0.05}
            value={comfort.seatedHeight}
            aria-label="Seated height offset"
            data-testid="setting-seated-height"
            onChange={(e) =>
              onChange({ comfort: { ...comfort, seatedHeight: Number(e.target.value) } })
            }
          />
          <span className="slider-value">{seated}</span>
        </label>
      </div>
      <p className="hint">
        Added to your real height in VR: raise it to see the table as if standing while you sit,
        lower it to bring a high table down.
      </p>

      <div className="menu-row">
        <span className="menu-label">Colours</span>
        <Choice
          label="Palette"
          name="palette"
          choices={PALETTE_IDS}
          value={settings.palette}
          render={(id) => PALETTE_LABELS[id]}
          onPick={(palette) => onChange({ palette })}
        />
      </div>
      <p className="hint">
        Colour-blind: Light plays orange-gold and Dark blue, the squares differ in brightness, and
        moves are lit blue against orange attacks. Pieces with their own textures keep them.
      </p>

      <div className="menu-row">
        <span className="menu-label">Tablet text</span>
        <Choice
          label="Tablet text size"
          name="tablet-text"
          choices={TABLET_TEXT_SIZES}
          value={settings.tabletText}
          render={(size) => TEXT_LABELS[size]}
          onPick={(tabletText) => onChange({ tabletText })}
        />
      </div>
      <p className="hint">
        Larger lettering on the table's tablets and the turn sign; the longer hints are shortened to
        fit.
      </p>
    </>
  );
}
